/**
 * RUEMOOD Category Navigation Enhancement
 * Provides smooth navigation and user feedback for category filtering
 */
class RuemoodCategoryNav {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
    this.checkCollectionStatus();
  }

  bindEvents() {
    // Add click event listeners to category buttons
    const categoryButtons = document.querySelectorAll('.ruemood-category-btn');
    categoryButtons.forEach(button => {
      button.addEventListener('click', this.handleCategoryClick.bind(this));
    });
  }

  handleCategoryClick(event) {
    const button = event.currentTarget;
    const collectionExists = button.dataset.collectionExists === 'true';
    const handle = (button.dataset.collectionHandle || '').toLowerCase();
    const isAll = handle === 'all' || handle === 'all-products';
    
    // DEBUG: Log all the data
    console.log('🔍 Category Click Debug:', {
      handle: handle,
      collectionExists: collectionExists,
      debugHandle: button.dataset.debugHandle,
      debugTarget: button.dataset.debugTarget,
      debugUrl: button.dataset.debugUrl,
      href: button.href
    });
    
    // Skip validation for Shop All button
    if (isAll) {
      console.log('✅ Shop All - allowing navigation');
      this.addLoadingState(button);
      return;
    }
    
    // Allow navigation to main category collections even if not detected as existing
    const mainCategories = [
      'japanese-shirt', 
      'japanese-sweatshirt', 
      'japanese-jackets', 
      'japanese-kimono-top', 
      'japanese-shorts', 
      'japanese-pants', 
      'japanese-shoes',
      'japanese-caps',
      'japanese-streetwear-hoodies'
    ];
    
    const isMainCategory = mainCategories.includes(handle);
    
    // If it's a main category, allow navigation regardless of existence check
    if (isMainCategory) {
      console.log('✅ Main Category - allowing navigation for:', handle);
      this.addLoadingState(button);
      return;
    }
    
    // For other collections, check if they exist
    if (!collectionExists) {
      console.log('❌ Collection not found - preventing navigation for:', handle);
      this.showCollectionMessage(button);
      return;
    }

    console.log('✅ Collection exists - allowing navigation for:', handle);
    // Add loading state for better UX
    this.addLoadingState(button);
  }

  addLoadingState(button) {
    // Remove active class from all buttons
    document.querySelectorAll('.ruemood-category-btn').forEach(btn => {
      btn.classList.remove('active');
    });
    
    // Add active class to clicked button
    button.classList.add('active');
    
    // Optional: Add loading spinner or text
    const originalText = button.textContent;
    button.style.opacity = '0.7';
    
    // Reset after page load (if navigation is successful)
    setTimeout(() => {
      button.style.opacity = '';
    }, 100);
  }

  showCollectionMessage(button) {
    const collectionHandle = button.dataset.collectionHandle;
    const message = `The "${button.textContent.trim()}" collection is not yet available. Please ensure the "${collectionHandle}" collection exists in your Shopify admin.`;
    
    // Create or update a notification
    this.showNotification(message, 'warning');
  }

  showNotification(message, type = 'info') {
    // Remove any existing notifications
    const existingNotification = document.querySelector('.ruemood-category-notification');
    if (existingNotification) {
      existingNotification.remove();
    }

    // Create new notification
    const notification = document.createElement('div');
    notification.className = `ruemood-category-notification ruemood-category-notification--${type}`;
    notification.setAttribute('role', 'status');
    notification.setAttribute('aria-live', 'polite');
    notification.setAttribute('aria-atomic', 'true');
    notification.innerHTML = `
      <div class="ruemood-category-notification__content">
        <span class="ruemood-category-notification__message">${message}</span>
        <button class="ruemood-category-notification__close" aria-label="Close notification">&times;</button>
      </div>
    `;

    // Insert notification after the category nav
    const categoryNav = document.querySelector('.ruemood-category-nav');
    if (categoryNav) {
      categoryNav.after(notification);

      // Auto-hide after 5 seconds
      setTimeout(() => {
        if (notification && notification.parentNode) {
          notification.remove();
        }
      }, 5000);

      // Close button functionality
      const closeButton = notification.querySelector('.ruemood-category-notification__close');
      closeButton.addEventListener('click', () => {
        notification.remove();
      });
    }
  }

  checkCollectionStatus() {
    // Log collection status for debugging
    const categoryButtons = document.querySelectorAll('.ruemood-category-btn');
    const collectionStatus = {};
    
    categoryButtons.forEach(button => {
      const handle = button.dataset.collectionHandle;
      const exists = button.dataset.collectionExists === 'true';
      collectionStatus[handle] = exists;
      
      // Add visual indicator for non-existent collections
      if (!exists) {
        button.setAttribute('title', `Collection "${handle}" not found. Click for more info.`);
      }
    });

    // Console logging for developers
    if (Object.keys(collectionStatus).length > 0) {
      console.log('RUEMOOD Category Navigation - Collection Status:', collectionStatus);
      
      const missingCollections = Object.entries(collectionStatus)
        .filter(([handle, exists]) => !exists)
        .map(([handle]) => handle);
      
      if (missingCollections.length > 0) {
        console.warn('Missing collections that need to be created in Shopify admin:', missingCollections);
      }
    }
  }
}

// CSS for notifications (injected via JavaScript to keep everything in one file)
const notificationStyles = `
  .ruemood-category-notification {
    background: rgb(var(--color-background));
    border: 1px solid rgb(var(--color-foreground) / 0.14);
    border-radius: 8px;
    margin: 16px 20px;
    max-width: 1440px;
    margin-left: auto;
    margin-right: auto;
    animation: slideDown 0.3s ease-out;
  }

  .ruemood-category-notification--warning {
    background: rgb(var(--color-foreground) / 0.06);
    border-color: rgb(var(--color-foreground) / 0.24);
    color: rgb(var(--color-foreground));
  }

  .ruemood-category-notification__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
  }

  .ruemood-category-notification__message {
    font-size: var(--text-sm);
    line-height: 1.4;
  }

  .ruemood-category-notification__close {
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    padding: 0;
    margin-left: 16px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  .ruemood-category-notification__close:hover {
    opacity: 1;
  }

  .ruemood-category-notification__close:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgb(var(--color-foreground) / 0.2);
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media screen and (max-width: 749px) {
    .ruemood-category-notification {
      margin: 12px 16px;
    }
    
    .ruemood-category-notification__content {
      padding: 10px 12px;
    }
    
    .ruemood-category-notification__message {
      font-size: var(--text-xs);
    }
  }
`;

// Inject styles
const styleSheet = document.createElement('style');
styleSheet.textContent = notificationStyles;
document.head.appendChild(styleSheet);

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new RuemoodCategoryNav();
  });
} else {
  new RuemoodCategoryNav();
}

// Export for potential use in other scripts
window.RuemoodCategoryNav = RuemoodCategoryNav;
// Clean behavior: rely on native anchor navigation and RuemoodCategoryNav handlers only
